//Shared by the on-screen report and its PDF, so the two can never disagree about a figure or a caveat.

export const money = value =>
  `$${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const quantity = value =>
  Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

export const percent = value =>
  Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

//the employer tax rate lives on each breakdown, so name it only when the whole period agrees
export const getLaborLabel = taxRates =>
  taxRates?.length === 1
    ? `Labor (incl. ${percent(taxRates[0] * 100)}% employer tax)`
    : 'Labor (incl. employer tax)';

const shareOf = (value, whole) => (whole ? (Number(value || 0) / whole) * 100 : 0);

export const getOperationsFigures = (totals, taxRates) => {
  const costs = [
    [getLaborLabel(taxRates), totals.labor],
    ['Material', totals.material],
    ['Fuel', totals.fuel],
    ['Insurance', totals.insurance],
  ];
  const totalCost = costs.reduce((sum, [, value]) => sum + Number(value || 0), 0);

  //each cost is a share of total cost, so the four add to 100%; the margin is a share of sales
  return {
    costRows: costs.map(([label, value]) => [label, value, shareOf(value, totalCost)]),
    totalCost,
    margin: shareOf(totals.profit, totals.sales),
  };
};

export const formatRunAt = value =>
  new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

export const STALENESS_NOTE =
  'These are the figures as they stood then, and do not reflect breakdowns submitted or changed since.';

export const coverageText = coverage =>
  `${coverage.jobsWithSubmittedBreakdown} of ${coverage.jobsInRange} jobs in this period have a submitted cost breakdown.`;

export const OPERATIONS_NOTES = [
  "Profit is sales less labor, material, fuel and insurance, with labor uplifted by the breakdown's employer tax rate.",
];

export const PAYROLL_NOTES = [
  'Pay is pools times pool rate plus travel and extras, as entered on each breakdown, before employer tax. A job counts in the period of its last shift, so work on earlier shifts can land in a later period.',
];

export const FINISH_LABELS = {
  PLASTER: 'Plaster',
  PEBBLE: 'Pebble',
  QUARTZ: 'Quartz',
  NOT_SET: 'Not set',
};

export const formatRates = rates => rates.map(money).join(' / ');

//A payroll run narrowed to some crews or people says so wherever it is shown, so it is never read as
//the whole branch. Runs from before the filter existed carry no `filter` at all.
export const getFilterLines = filter => {
  if (!filter) return [];

  const lines = [];
  if (filter.crews?.length) {
    lines.push(
      `Crews: ${filter.crews.map(crew => crew.name).join(', ')}. Only jobs these crews worked a shift of are counted, along with everyone paid on them.`,
    );
  }
  if (filter.employees?.length) {
    lines.push(`Employees: ${filter.employees.join(', ')}. Contractors are left out.`);
  }
  return lines;
};

//The combined section leads only when several locations were run, and passes no employees, since
//namesakes at two branches are not one person. Gunite branches report yards and plaster branches
//batches, so a mixed selection shows both in the combined section.
export const getSections = (report, period) => {
  const anyGunite = report.locations.some(section => section.location.service === 'GUNITE');
  const anyPlaster = report.locations.some(section => section.location.service === 'PLASTER');

  const combined =
    report.locations.length > 1
      ? [
          {
            key: 'combined',
            heading: 'All selected locations',
            subheading: `${period.label} · ${report.locations.length} locations`,
            totals: report.combined.totals,
            taxRates: report.combined.taxRates,
            coverage: report.combined.coverage,
            showYards: anyGunite,
            showBatches: anyPlaster,
            showContractors: anyPlaster,
          },
        ]
      : [];

  return [
    ...combined,
    ...report.locations.map(section => ({
      key: section.location.id,
      heading: section.location.label,
      subheading: `${period.label} · ${section.location.service}`,
      totals: section.totals,
      taxRates: section.taxRates,
      coverage: section.coverage,
      showYards: section.location.service !== 'PLASTER',
      showBatches: section.location.service === 'PLASTER',
      showContractors: section.location.service === 'PLASTER',
      employees: section.employees,
      contractors: section.contractors,
    })),
  ];
};
